import type {
  CategoryMapping,
  CategoryMappingDraft,
  CategoryMappingResponse,
  CategoryScan,
  FeedPreview,
} from '../models/product.types'

/** Legacy CategoryMapping.vue:330-352 — omit channels whose selections are all empty. */
export function buildPayload(draft: CategoryMappingDraft): CategoryMapping {
  const payload: CategoryMapping = {}
  for (const [channel, categories] of Object.entries(draft)) {
    const selected = Object.fromEntries(
      Object.entries(categories).filter((entry): entry is [string, string] => Boolean(entry[1])),
    )
    if (Object.keys(selected).length > 0) payload[channel] = selected
  }
  return payload
}

export function normalizePreview(value: unknown): FeedPreview {
  if (!value || typeof value !== 'object') return { rows: [], columns: [] }
  const source = value as { preview?: unknown; columns?: unknown }
  const rows = Array.isArray(source.preview)
    ? source.preview.filter(
        (row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object',
      )
    : []
  const columns = Array.isArray(source.columns)
    ? source.columns
        .map((column) => (Array.isArray(column) ? column[0] : column))
        .filter((column): column is string => typeof column === 'string')
    : []
  return { rows, columns }
}

export function categoryOptions(scan: CategoryScan): string[] {
  return [...new Set(Object.values(scan).flat().filter(Boolean))]
}

function existingEntries(value: CategoryMappingResponse): Array<[string, string, string]> {
  if (Array.isArray(value)) {
    return value.map((entry) => [
      entry.marketing_channel,
      entry.old_product_category,
      entry.new_product_category,
    ])
  }
  return Object.entries(value).flatMap(([channel, mapping]) =>
    Object.entries(mapping).map<[string, string, string]>(([oldCategory, newCategory]) => [
      channel,
      oldCategory,
      newCategory,
    ]),
  )
}

export function buildDraft(
  channelScan: CategoryScan,
  existing: CategoryMappingResponse,
  options: readonly string[],
): CategoryMappingDraft {
  const draft: CategoryMappingDraft = {}
  for (const [channel, categories] of Object.entries(channelScan)) {
    if (categories.length === 0) continue
    draft[channel] = Object.fromEntries(categories.map((category) => [category, null]))
  }
  const allowed = new Set(options)
  for (const [channel, oldCategory, newCategory] of existingEntries(existing)) {
    if (draft[channel]?.[oldCategory] !== undefined && allowed.has(newCategory)) {
      draft[channel][oldCategory] = newCategory
    }
  }
  return draft
}

export function displayColumn(field: string): string {
  return field
    .replace(/^(?:meta_|gpc_productattributes_|gpc_|ga4_)/u, '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}
